/**
 * SPのメニューの開け閉め
 *
 * @description ボタンとメニューは .wrapper の外にあるので非同期遷移で作り直されない。
 *              読み込み時に一度だけ配線して、以後は開閉の状態だけを持つ。
 *
 *              見た目の切り替えは html の is-drawerOpen に対するCSSの遷移が持つ
 *              （_common_header.scss）。ここが書くのは状態とフォーカスだけで、
 *              板の伸びや中身の立ち上がりの秒数・曲線には触らない。
 */

/** メニューが開いているあいだ html に付ける。見た目も裏のスクロール止めもこれ1つで決まる */
const OPEN = 'is-drawerOpen';

/** Tabで回す対象。板の中とボタンだけを行き来させる */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 解除する手続き。二重に配線しないために持っておく */
let detach: (() => void) | null = null;

/** 開く前にフォーカスがあった要素。閉じたら戻す */
let restoreTo: HTMLElement | null = null;

/**
 * 開閉に関わる要素をまとめて取る
 *
 * @returns 揃っていなければ null
 */
const pick = () => {
    const btn = document.querySelector<HTMLButtonElement>('[data-drawer-toggle]');
    const drawer = document.querySelector<HTMLElement>('[data-drawer]');
    const panel = drawer?.querySelector<HTMLElement>('[data-drawer-panel]');

    if (!btn || !drawer || !panel) return null;

    return { btn, drawer, panel };
};

/**
 * 今開いているか
 *
 * @returns 開いていれば true
 */
const isOpen = (): boolean => document.documentElement.classList.contains(OPEN);

/**
 * 開閉の状態を書く
 *
 * @param btn  開閉ボタン
 * @param next true で開く
 * @returns {void}
 */
const setState = (btn: HTMLButtonElement, next: boolean): void => {
    document.documentElement.classList.toggle(OPEN, next);
    btn.setAttribute('aria-expanded', String(next));

    const label = btn.querySelector('[data-drawer-label]');

    if (label) label.textContent = next ? 'メニューを閉じる' : 'メニューを開く';
};

/**
 * メニューを閉じる
 *
 * @description 開いていないときに呼んでも何も起きない。遷移のたびに呼ばれる想定
 * @returns {void}
 */
const closeDrawer = (): void => {
    const el = pick();

    if (!el || !isOpen()) return;

    setState(el.btn, false);

    // 開いたきっかけの要素へ戻す。ボタン以外から開くことは無いが、
    // 遷移で板ごと消えたときにフォーカスが body へ落ちるのを防ぐ
    restoreTo?.focus();
    restoreTo = null;
};

/**
 * メニューを開く
 *
 * @returns {void}
 */
const openDrawer = (): void => {
    const el = pick();

    if (!el || isOpen()) return;

    // 何も掴んでいない状態（activeElement が body）から開くことのほうが多い。
    // そのまま戻すとフォーカスが文書の先頭へ落ちるので、開閉を持つボタンを既定にする
    const from = document.activeElement;

    restoreTo = from instanceof HTMLElement && from !== document.body ? from : el.btn;

    setState(el.btn, true);

    // 閉じているあいだの板は visibility: hidden で、そのままではフォーカスが当たらない。
    // 位置を読んでスタイルを確定させてから掴みにいく
    void el.panel.offsetWidth;

    el.panel.querySelector<HTMLElement>(FOCUSABLE)?.focus();
};

/**
 * メニューの配線を外す
 *
 * @returns {void}
 */
const destroyDrawer = (): void => {
    closeDrawer();
    detach?.();
    detach = null;
};

/**
 * メニューの開け閉めを配線する
 *
 * @returns {void}
 */
const initDrawer = (): void => {
    detach?.();
    detach = null;

    const el = pick();

    if (!el) return;

    const { btn, drawer, panel } = el;

    const onToggle = (): void => {
        if (isOpen()) {
            closeDrawer();
        } else {
            openDrawer();
        }
    };

    // 板の外（ぼかした地）を触ったら閉じる
    const onBackdrop = (e: MouseEvent): void => {
        if (e.target === drawer) closeDrawer();
    };

    // リンクを押したらそのまま遷移するので、先に閉じておく。
    // 別タブで開くものも、戻ってきたときに開いたままだと迷う
    const onLink = (e: MouseEvent): void => {
        if ((e.target as Element | null)?.closest('a[href]')) closeDrawer();
    };

    const onKey = (e: KeyboardEvent): void => {
        if (!isOpen()) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            closeDrawer();
            return;
        }

        if (e.key !== 'Tab') return;

        // ボタンは板の前にあるので、輪の先頭として扱う
        const ring = [btn, ...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const first = ring[0];
        const last = ring[ring.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    btn.addEventListener('click', onToggle);
    drawer.addEventListener('click', onBackdrop);
    panel.addEventListener('click', onLink);
    document.addEventListener('keydown', onKey);

    detach = () => {
        btn.removeEventListener('click', onToggle);
        drawer.removeEventListener('click', onBackdrop);
        panel.removeEventListener('click', onLink);
        document.removeEventListener('keydown', onKey);
    };
};

export { initDrawer, destroyDrawer, closeDrawer };

document.addEventListener('DOMContentLoaded', initDrawer);
